/**
 * Typed API endpoint functions. Each unwraps the backend's { data } envelope
 * and maps snake_case payloads into the frontend's domain shapes via api-map.
 * These are only called when NOT in demo mode (see queries.ts / store.ts).
 */

import { api } from "./api-client";
import {
  mapOrder,
  mapProduct,
  mapReservation,
  mapUser,
  type ApiOrder,
  type ApiProduct,
  type ApiReservation,
  type ApiUser,
} from "./api-map";
import type { ApiResponse } from "@/types/api";
import type { Order, Product, Reservation } from "@/types/domain";
import type { User } from "@/types/auth";

function unwrap<T>(res: { data: ApiResponse<T> }): T {
  return res.data.data;
}

// ── Auth ──

export interface TokenBundle {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

interface ApiTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: ApiUser;
}

export const authApi = {
  async sendOTP(phone: string): Promise<void> {
    await api.post("/auth/otp/send", { phone });
  },

  async verifyOTP(phone: string, code: string, role?: string): Promise<TokenBundle> {
    const res = await api.post<ApiResponse<ApiTokenResponse>>("/auth/otp/verify", {
      phone,
      code,
      ...(role ? { role } : {}),
    });
    const d = unwrap(res);
    return {
      accessToken: d.access_token,
      refreshToken: d.refresh_token,
      expiresIn: d.expires_in,
      user: mapUser(d.user),
    };
  },

  async refresh(refreshToken: string): Promise<{ accessToken: string; refreshToken: string; expiresIn: number }> {
    const res = await api.post<ApiResponse<ApiTokenResponse>>("/auth/refresh", {
      refresh_token: refreshToken,
    });
    const d = unwrap(res);
    return { accessToken: d.access_token, refreshToken: d.refresh_token, expiresIn: d.expires_in };
  },

  async logout(refreshToken: string): Promise<void> {
    await api.post("/auth/logout", { refresh_token: refreshToken });
  },

  async me(): Promise<User> {
    const res = await api.get<ApiResponse<ApiUser>>("/auth/me");
    return mapUser(unwrap(res));
  },

  async updateProfile(businessName: string, market: string): Promise<User> {
    const res = await api.patch<ApiResponse<ApiUser>>("/auth/profile", {
      business_name: businessName,
      market,
    });
    return mapUser(unwrap(res));
  },
};

// ── Products & stock ──

export const productApi = {
  async list(): Promise<Product[]> {
    const res = await api.get<ApiResponse<ApiProduct[]>>("/products");
    return (unwrap(res) ?? []).map((p) => mapProduct(p));
  },

  async get(id: string): Promise<Product | null> {
    const res = await api.get<ApiResponse<ApiProduct>>(`/products/${id}`);
    const d = unwrap(res);
    return d ? mapProduct(d) : null;
  },

  async create(input: {
    name: string;
    brand: string;
    category: string;
    description: string;
    condition: string;
    price: number;
    is_visible: boolean;
    sizes: { size: string; quantity: number }[];
  }): Promise<Product> {
    const res = await api.post<ApiResponse<ApiProduct>>("/products", input);
    return mapProduct(unwrap(res));
  },

  async update(
    id: string,
    input: { name: string; brand: string; category: string; description: string; condition: string; price: number; is_visible: boolean },
  ): Promise<Product> {
    const res = await api.patch<ApiResponse<ApiProduct>>(`/products/${id}`, input);
    return mapProduct(unwrap(res));
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/products/${id}`);
  },

  async updateStock(id: string, sizes: { size: string; quantity: number }[]): Promise<void> {
    await api.patch(`/products/${id}/stock`, { sizes });
  },

  async uploadImage(id: string, file: File): Promise<string[]> {
    const form = new FormData();
    form.append("image", file);
    const res = await api.post<ApiResponse<{ image_urls: string[] }>>(`/products/${id}/image`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return unwrap(res).image_urls ?? [];
  },

  async deleteImage(id: string, index: number): Promise<string[]> {
    const res = await api.delete<ApiResponse<{ image_urls: string[] }>>(`/products/${id}/images/${index}`);
    return unwrap(res).image_urls ?? [];
  },
};

// ── Reservations & orders ──

export const orderApi = {
  async reserve(stockId: string, quantity: number): Promise<Reservation> {
    const res = await api.post<ApiResponse<ApiReservation>>("/reservations", {
      stock_id: stockId,
      quantity,
    });
    return mapReservation(unwrap(res));
  },

  async releaseReservation(id: string): Promise<void> {
    await api.delete(`/reservations/${id}`);
  },

  async cart(): Promise<Reservation[]> {
    const res = await api.get<ApiResponse<ApiReservation[]>>("/reservations");
    return (unwrap(res) ?? []).map(mapReservation);
  },

  async checkout(): Promise<Order[]> {
    const res = await api.post<ApiResponse<ApiOrder[]>>("/orders", {});
    return (unwrap(res) ?? []).map(mapOrder);
  },

  async list(): Promise<Order[]> {
    const res = await api.get<ApiResponse<ApiOrder[]>>("/orders");
    return (unwrap(res) ?? []).map(mapOrder);
  },

  async updateStatus(id: string, status: string): Promise<void> {
    await api.patch(`/orders/${id}/status`, { status });
  },

  async cancelOrder(id: string): Promise<void> {
    await api.delete(`/orders/${id}`);
  },
};

// ── Wholesaler directory + connections (reseller) ──

export interface DirectoryWholesaler {
  id: string;
  businessName: string;
  market: string;
  tier: string;
  productCount: number;
  connected: boolean;
}

interface ApiDirectoryWholesaler {
  id: string;
  business_name: string;
  market: string;
  subscription_tier: string;
  product_count: number;
  connected: boolean;
}

export const connectionApi = {
  async listWholesalers(search?: string): Promise<DirectoryWholesaler[]> {
    const res = await api.get<ApiResponse<ApiDirectoryWholesaler[]>>("/wholesalers", {
      params: search ? { search } : {},
    });
    return (unwrap(res) ?? []).map((w) => ({
      id: w.id,
      businessName: w.business_name,
      market: w.market,
      tier: w.subscription_tier,
      productCount: w.product_count,
      connected: w.connected,
    }));
  },

  async connect(wholesalerID: string): Promise<void> {
    await api.post("/connections", { wholesaler_id: wholesalerID });
  },

  async disconnect(wholesalerID: string): Promise<void> {
    await api.delete(`/connections/${wholesalerID}`);
  },
};

// ── Admin (platform admin only) ──

export interface AdminMember {
  id: string;
  role: string;
  businessName: string;
  phone: string;
  market: string;
  subscriptionTier: string;
  subscriptionExpiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOverview {
  totalMembers: number;
  totalWholesalers: number;
  totalResellers: number;
}

interface ApiAdminMember {
  id: string;
  role: string;
  business_name: string;
  phone: string;
  market: string;
  subscription_tier: string;
  subscription_expires_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ApiAdminOverview {
  total_members: number;
  total_wholesalers: number;
  total_resellers: number;
}

interface ApiAuditEntry {
  id: string;
  table_name: string;
  record_id: string | null;
  operation: string;
  changed_by: string;
  actor_role: string;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  changed_at: string;
}

export interface AdminAuditEntry {
  id: string;
  tableName: string;
  recordId: string | null;
  operation: string;
  changedBy: string;
  actorRole: string;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  changedAt: string;
}

export const adminApi = {
  async listMembers(search?: string): Promise<AdminMember[]> {
    const res = await api.get<ApiResponse<ApiAdminMember[]>>("/admin/members", {
      params: search ? { search } : {},
    });
    return (unwrap(res) ?? []).map((m) => ({
      id: m.id,
      role: m.role,
      businessName: m.business_name,
      phone: m.phone,
      market: m.market,
      subscriptionTier: m.subscription_tier,
      subscriptionExpiresAt: m.subscription_expires_at,
      isActive: m.is_active,
      createdAt: m.created_at,
      updatedAt: m.updated_at,
    }));
  },

  async getOverview(): Promise<AdminOverview> {
    const res = await api.get<ApiResponse<ApiAdminOverview>>("/admin/overview");
    const d = unwrap(res);
    return {
      totalMembers: d.total_members,
      totalWholesalers: d.total_wholesalers,
      totalResellers: d.total_resellers,
    };
  },

  async getAudit(params?: { table?: string; action?: string; limit?: number; offset?: number }): Promise<AdminAuditEntry[]> {
    const res = await api.get<ApiResponse<ApiAuditEntry[]>>("/admin/audit", { params: params ?? {} });
    return (unwrap(res) ?? []).map((e) => ({
      id: e.id,
      tableName: e.table_name,
      recordId: e.record_id ?? null,
      operation: e.operation,
      changedBy: e.changed_by,
      actorRole: e.actor_role,
      oldData: e.old_data ?? null,
      newData: e.new_data ?? null,
      changedAt: e.changed_at,
    }));
  },

  async listOrders(): Promise<Order[]> {
    const res = await api.get<ApiResponse<ApiOrder[]>>("/admin/orders");
    return (unwrap(res) ?? []).map(mapOrder);
  },

  async overrideOrderStatus(id: string, status: string): Promise<void> {
    await api.patch(`/admin/orders/${id}/status`, { status });
  },

  async deleteMember(id: string): Promise<void> {
    await api.delete(`/admin/members/${id}`);
  },

  async createMember(input: {
    phone: string;
    role: string;
    businessName: string;
    market?: string;
    subscriptionTier?: string;
  }): Promise<AdminMember> {
    const res = await api.post<ApiResponse<ApiAdminMember>>("/admin/members", {
      phone: input.phone,
      role: input.role,
      business_name: input.businessName,
      market: input.market ?? "",
      subscription_tier: input.subscriptionTier ?? "free",
    });
    const m = unwrap(res);
    return {
      id: m.id,
      role: m.role,
      businessName: m.business_name,
      phone: m.phone,
      market: m.market,
      subscriptionTier: m.subscription_tier,
      subscriptionExpiresAt: m.subscription_expires_at,
      isActive: m.is_active,
      createdAt: m.created_at,
      updatedAt: m.updated_at,
    };
  },

  async updateMember(
    id: string,
    patch: { businessName?: string; market?: string; subscriptionTier?: string; isActive?: boolean },
  ): Promise<AdminMember> {
    const body: Record<string, unknown> = {};
    if (patch.businessName !== undefined) body.business_name = patch.businessName;
    if (patch.market !== undefined) body.market = patch.market;
    if (patch.subscriptionTier !== undefined) body.subscription_tier = patch.subscriptionTier;
    if (patch.isActive !== undefined) body.is_active = patch.isActive;
    const res = await api.patch<ApiResponse<ApiAdminMember>>(`/admin/members/${id}`, body);
    const m = unwrap(res);
    return {
      id: m.id,
      role: m.role,
      businessName: m.business_name,
      phone: m.phone,
      market: m.market,
      subscriptionTier: m.subscription_tier,
      subscriptionExpiresAt: m.subscription_expires_at,
      isActive: m.is_active,
      createdAt: m.created_at,
      updatedAt: m.updated_at,
    };
  },
};

// ── API keys (reseller storefront) ──

export interface ApiKey {
  id: string;
  name: string;
  masked: string;
  lastUsedAt: string | null;
  createdAt: string;
}

interface ApiApiKey {
  id: string;
  name: string;
  masked: string;
  last_used_at: string | null;
  created_at: string;
}

function mapApiKey(k: ApiApiKey): ApiKey {
  return {
    id: k.id,
    name: k.name,
    masked: k.masked,
    lastUsedAt: k.last_used_at,
    createdAt: k.created_at,
  };
}

export const apiKeyApi = {
  async list(): Promise<ApiKey[]> {
    const res = await api.get<ApiResponse<ApiApiKey[]>>("/api-keys");
    return (unwrap(res) ?? []).map(mapApiKey);
  },

  /** Returns the plaintext key (shown once) plus the stored record. */
  async create(name?: string): Promise<{ key: string; record: ApiKey }> {
    const res = await api.post<ApiResponse<{ key: string; api_key: ApiApiKey }>>("/api-keys", {
      name: name ?? "",
    });
    const d = unwrap(res);
    return { key: d.key, record: mapApiKey(d.api_key) };
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/api-keys/${id}`);
  },
};

// ── Analytics ──

export const analyticsApi = {
  async summary(days = 30): Promise<{ totalRevenue: number; unitsSold: number; avgSale: number; turnoverRate: number }> {
    const res = await api.get<ApiResponse<{
      total_revenue: number;
      units_sold: number;
      average_sale_value: number;
      turnover_rate: number;
    }>>("/analytics/summary", { params: { days } });
    const d = unwrap(res);
    return {
      totalRevenue: d.total_revenue,
      unitsSold: d.units_sold,
      avgSale: Math.round(d.average_sale_value),
      turnoverRate: d.turnover_rate,
    };
  },
};
